import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find(c => c.slug === slug);

  let filteredComics = slug === 'all'
    ? comics
    : comics.filter(c => c.category === slug);

  if (sortBy === 'popular') {
    filteredComics = [...filteredComics].sort((a, b) => b.views - a.views);
  } else if (sortBy === 'rating') {
    filteredComics = [...filteredComics].sort((a, b) => b.rating - a.rating);
  } else {
    filteredComics = [...filteredComics].sort(
      (a, b) => new Date(b.chapters[b.chapters.length - 1].updateDate).getTime() -
               new Date(a.chapters[a.chapters.length - 1].updateDate).getTime()
    );
  }

  const categoryName = currentCategory ? currentCategory.name : '全部漫画';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: window.location.origin + (__BASE_PATH__ || ''),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: window.location.origin + (__BASE_PATH__ || '') + '/category/' + slug,
      },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 韩漫屋韩国免费漫画`,
    description: `韩漫屋${categoryName}漫画分类，提供完整无删减的韩国漫画免费在线阅读，海量资源持续更新。`,
    url: window.location.origin + (__BASE_PATH__ || '') + '/category/' + slug,
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-3 bg-background-50">
          <nav className="max-w-7xl mx-auto flex items-center gap-1.5 text-xs text-foreground-600" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950">{categoryName}</span>
          </nav>
        </div>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {categoryName}
              <span className="text-sm font-normal text-foreground-600 ml-2">
                共 {filteredComics.length} 部作品
              </span>
            </h1>
            <p className="text-sm text-foreground-600 mt-2 max-w-lg">
              {slug === 'all'
                ? '浏览韩漫屋全部韩国漫画资源，涵盖恋爱、奇幻、动作、悬疑等丰富分类，全部免费在线阅读。'
                : `韩漫屋${categoryName}类漫画精选，为您提供最优质的${categoryName}韩漫免费在线阅读体验。`}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 sticky top-14 z-40 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto flex flex-wrap gap-1.5 py-2 overflow-x-auto">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-accent-500 text-background-50'
                    : 'text-foreground-600 hover:bg-accent-100 hover:text-accent-700'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Sort Controls */}
        <section className="w-full px-4 md:px-6 py-3 bg-background-50">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <span className="text-xs text-foreground-600">排序：</span>
            {[
              { key: 'latest' as const, label: '最新' },
              { key: 'popular' as const, label: '最热' },
              { key: 'rating' as const, label: '评分' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`whitespace-nowrap px-3 py-1 rounded-full text-xs cursor-pointer transition-colors ${
                  sortBy === opt.key
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        {/* Comic Grid */}
        <section className="w-full px-4 md:px-6 py-6 pb-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-600"></i>
                </div>
                <p className="text-foreground-600 text-sm">暂无该分类的漫画，敬请期待！</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - ${categoryName}韩漫免费阅读`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          comic.status === '连载中'
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-foreground-600 text-background-50'
                        }`}>
                          {comic.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <div className="flex items-center gap-2 text-white text-xs">
                          <span><i className="ri-star-fill text-accent-500 mr-0.5"></i>{comic.rating}</span>
                          <span><i className="ri-eye-line mr-0.5"></i>{(comic.views / 10000).toFixed(0)}万</span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-foreground-600">
                      <span>{comic.author}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
    </>
  );
}